import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import type { EditorProps } from '@atlaskit/editor-core';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension';
import { usePreviousState } from '@atlaskit/editor-common/hooks';
import { type ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility';

import type { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';

import { useIsMentionOnRestrictedPageEligible } from '@confluence/experiment-mention-on-restricted-page';
import type { LoomProviderConfig } from '@confluence/fabric-providers';
import { expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import {
	type MentionType,
	useMentionsNotifications,
	handleMentionsChanged,
} from '@confluence/mentions-notifications';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api';
import { useSessionData } from '@confluence/session-data';
import type { WithFlagsProps } from '@confluence/flags';
import type { EditorAPI } from '@confluence/loom-editor-utils';
import { useInitializeLoomPlugin, useLoomPluginOptions } from '@confluence/loom-editor-utils';
import {
	useEditorEngagementPlatform,
	useEditorPluginAIDefinitionsConfig,
	useEditorPluginAIDefinitionsEnabled,
	useEditorPluginAIProactiveConfig,
	useEditorPluginAIProactiveEnabled,
	useSelectionExtensionPluginOptions,
} from '@confluence/editor-features';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import type { AIGlobalOptIn, EditorPluginAIFeedbackHandlersRef } from '@confluence/editor-features';
import type { FullPageAppearances } from '@confluence/content-appearance';
import { useIsSidePanelOpen } from '@confluence/side-panel-state-container';
import { type PageMode, getPageMode } from '@confluence/issue-create-side-panel';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useIsEligibleCreateNewWhiteboard } from '@confluence/experiment-prompt-whiteboard-competitor-link';

import { useEditorPreferencesProvider } from '../utils/EditorPreferences';

import { EditorLoomButton } from './EditorLoomButton';
import {
	type CreateFullPageEditorPresetProps,
	createFullPageEditorPreset,
} from './createFullPageEditorPresetNext';
export interface FullPageEditorPresetProps extends WithFlagsProps {
	appearance: FullPageAppearances;
	featureFlags: EditorProps['featureFlags'];
	onSave: EditorProps['onSave'];
	linking: EditorProps['linking'];
	disabled?: EditorProps['disabled'];
	contextIdentifierProvider: EditorProps['contextIdentifierProvider'];
	media: EditorProps['media'];
	placeholder: EditorProps['placeholder'];
	emojiProvider: EditorProps['emojiProvider'];
	annotationProviders: EditorProps['annotationProviders'];
	autoformattingProvider: EditorProps['autoformattingProvider'];
	taskDecisionProvider: EditorProps['taskDecisionProvider'];
	collabEditProvider: EditorProps['collabEditProvider'];
	mentionProvider: EditorProps['mentionProvider'];
	collabEdit: EditorProps['collabEdit'];
	primaryToolbarComponents: EditorProps['primaryToolbarComponents'];
	waitForMediaUpload: EditorProps['waitForMediaUpload'];
	mention: EditorProps['mention'];
	allowFindReplace: EditorProps['allowFindReplace'];
	allowExtension: EditorProps['allowExtension'];
	extensionHandlers: EditorProps['extensionHandlers'];
	referentialityContext: ReferentialityContext;
	editorPluginAIOptions: ReturnType<typeof createPageEditorPluginAIOptions>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	editorPluginAIFeedbackHandlers: React.RefObject<EditorPluginAIFeedbackHandlersRef>;
	elementBrowser: EditorProps['elementBrowser'];
	quickInsert: EditorProps['quickInsert'];
	setEditorAPI?: (editorAPI?: FullPageEditorPresetAPINext) => void;
	viewMode?: ViewMode;
	hasEditPermission?: boolean;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
	getHeadingLinkPath: () => string | null;
	__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'];
	__livePage?: boolean;
	contextPanelOptions?: ContextPanelPluginOptions;
	contentId: string | undefined;
	spaceId: string | undefined;
	isAIEnabledForContent: boolean;
	getExtensionHeight?: GetPMNodeHeight;
	contextualFormattingEnabled: boolean;
	isCompanyHub?: boolean;
}

export type FullPageEditorPresetAPINext = ExtractPresetAPI<
	ReturnType<typeof useFullPageEditorPreset>
>;

// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function useFullPageEditorPreset(props: FullPageEditorPresetProps): any {
	const { showObjectSidebar, hideObjectSidebar, hideObjectSidebarByPanelId } =
		useObjectSidebarActions();

	const isLivePage = useIsCurrentPageLive();
	const { isDesktop } = useIsDesktop();

	const { userId } = useSessionData();
	const [
		{
			deletedMentions,
			addedMentions,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			addedTasks,
			deletedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
		},
		{
			setDeletedMentions,
			setAddedMentions,
			setAddedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();
	const [mentionChanges, setMentionChanges] = useState<MentionType[]>([]);
	const previousEditorProps = usePreviousState(props);
	const isClassicMentionsNotificationsEnrolled =
		useIsMentionOnRestrictedPageEligible() &&
		expValNoExposure('cc_onboarding_mention_on_restricted_page', 'cohort', 'not-enrolled') !==
			'not-enrolled';

	const isLivePageRef = useRef<boolean>(isLivePage);
	isLivePageRef.current = isLivePage;

	const getCurrentIsLivePage = useCallback(() => {
		return isLivePageRef.current;
	}, []);

	const { confluenceEditorPreferencesProvider } = useEditorPreferencesProvider({
		getCurrentIsLivePage,
	});

	const isSidePanelOpen = useIsSidePanelOpen();
	const isSidePanelOpenRef = useRef<boolean>(isSidePanelOpen);
	isSidePanelOpenRef.current = isSidePanelOpen;

	const rightSidebarContext = useRightSidebarContext();
	const rightSidebarContextRef = useRef(rightSidebarContext);
	rightSidebarContextRef.current = rightSidebarContext;

	const hasEditPermission = props.hasEditPermission || false;
	const hasEditPermissionRef = useRef<boolean>(hasEditPermission);
	hasEditPermissionRef.current = hasEditPermission;

	const pageMode = getPageMode(props.viewMode, props.__livePage, props.hasEditPermission);
	const pageModeRef = useRef<PageMode>(pageMode);
	pageModeRef.current = pageMode;

	useEffect(() => {
		const isMentionNotificationsEnabled =
			isLivePage || (!isLivePage && isClassicMentionsNotificationsEnrolled);

		if (mentionChanges.length && isMentionNotificationsEnabled) {
			handleMentionsChanged({
				mentionChanges,
				deletedMentions,
				addedMentions,
				setDeletedMentions,
				setAddedMentions,
				userId,
				unrestrictedMentions,
				parentSpaceRestrictedMentions,
				pageRestrictedMentions,
				unrestrictedTasks,
				addedTasks,
				deletedTasks,
				setAddedTasks,
				setDeletedTasks,
				pageRestrictedTasks,
				parentSpaceRestrictedTasks,
				setMentionsAdded,
				setTasksAdded,
			});
			setMentionChanges([]);
		}
		// We only need to trigger the handler function when mentionChanges gets updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionChanges]);

	const editorAPIRef = useRef<FullPageEditorPresetAPINext | null | undefined>(null);

	const { isPluginEnabled: isLoomPluginEnabled, pluginOptions: loomPluginOptions } =
		useLoomPluginOptions({
			EditorButtonComponent: EditorLoomButton,
			editorAPIRef,
			// [ED-27497] IMPORTANT: If you change the code below, please also update the
			// platform/packages/editor/editor-presets-confluence/src/presets/full-page/loomPluginOptions.ts
			...(fg('confluence_frontend_object_sidebar')
				? { editorShouldShowToolbarLoomButton: false }
				: {}),
		});

	const { config: editorEngagementPlatformPluginConfig } = useEditorEngagementPlatform();

	const editorPluginAIDefinitionsEnabled = useEditorPluginAIDefinitionsEnabled();
	const editorPluginAIDefinitionsConfig = useEditorPluginAIDefinitionsConfig();

	const editorPluginAIProactiveEnabled = useEditorPluginAIProactiveEnabled({
		aiGlobalOptInStatus: props.aiGlobalOptIn.status,
	});

	const editorPluginAIProactiveConfig = useEditorPluginAIProactiveConfig({
		aiGlobalOptIn: props.aiGlobalOptIn,
		editorPluginAIFeedbackHandlers: props.editorPluginAIFeedbackHandlers,
	});

	const selectionExtensionPluginOptions = useSelectionExtensionPluginOptions({
		isSidePanelOpenRef,
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
	});

	const { isEligibleCreateNewWhiteboard } = useIsEligibleCreateNewWhiteboard();

	const intl = useIntl();

	const presetFunction = useCallback(() => {
		const {
			__livePage,
			__rendererExtensionOptions,
			allowFindReplace,
			annotationProviders,
			appearance,
			autoformattingProvider,
			editorPluginAIOptions,
			collabEdit,
			contextIdentifierProvider,
			elementBrowser,
			emojiProvider,
			extensionHandlers,
			getExtensionHeight,
			getHeadingLinkPath,
			hasRequestedEditPermission,
			isAIEnabledForContent,
			linking,
			media,
			mention,
			mentionProvider,
			quickInsert,
			referentialityContext,
			requestToEditContent,
			taskDecisionProvider,
			viewMode,
			disabled,
		} = props;

		// IMPORTANT: Don't add spread operator (`...props`) here because it will make impossible to
		//            understand which props are used in the preset.
		const presetOptions: CreateFullPageEditorPresetProps = {
			__livePage,
			__rendererExtensionOptions,
			allowFindReplace,
			annotationProviders,
			appearance,
			autoformattingProvider,
			collabEdit,
			contextIdentifierProvider,
			contextPanelOptions: {
				objectSideBar: {
					showPanel: showObjectSidebar,
					closePanel: hideObjectSidebar,
					closePanelById: hideObjectSidebarByPanelId,
				},
			},
			editorEngagementPlatformPluginConfig,
			editorPluginAIDefinitionsConfig,
			editorPluginAIDefinitionsEnabled,
			editorPluginAIOptions,
			editorPluginAIProactiveConfig,
			editorPluginAIProactiveEnabled,
			elementBrowser,
			emojiProvider,
			extensionHandlers,
			getExtensionHeight,
			getHeadingLinkPath,
			handleMentionsChanged: (changes: MentionType[]) => {
				if (changes.length) {
					setMentionChanges(changes);
				}
			},
			hasEditPermission: props.hasEditPermission,
			hasRequestedEditPermission,
			intl,
			isAIEnabledForContent,
			isCompanyHub: props.isCompanyHub || false,
			isDesktop,
			isEligibleCreateNewWhiteboard,
			isLoomPluginEnabled,
			linking,
			loomPluginOptions,
			media,
			mention,
			mentionProvider,
			prevAppearance: previousEditorProps?.appearance,
			quickInsert,
			referentialityContext,
			requestToEditContent,
			selectionExtensionPluginOptions,
			taskDecisionProvider,
			userPreferencesProvider: confluenceEditorPreferencesProvider,
			viewMode,
			contextualFormattingEnabled: props.contextualFormattingEnabled,
			disabled,
		};

		return createFullPageEditorPreset(presetOptions);
		// The dependency array should mirror the dependencies we pass to usePreset.
		// Refactor this to only use one usePreset when cleaning up confluence_frontend_transition_preloading_system FG
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.appearance, props.contentId, props.collabEditProvider]);

	const { preset: presetStandard, editorApi: editorApiStandard } = usePreset(presetFunction, [
		// WARNING: Adding this dependency array causes the editor plugins to all recreate
		// This can be very expensive. Be very careful adding to this, avoid if possible!
		// Generally we can update props via their plugin APIs (see `placeholder` as an example)
		// update preset based on props change, originally from packages/editor/editor-core/src/create-editor/preset-utils.ts
		// persistScrollGutter not used, allowUndoRedoButtons and sanitizePrivateContent always true
		props.appearance,
	]);
	const { preset: presetSmootherTransitionsFG, editorApi: editorApiSmootherTransitionsFG } =
		usePreset(presetFunction, [props.appearance, props.contentId, props.collabEditProvider]);

	const isPresetRerunningOnTransition =
		fg('confluence_frontend_transition_preloading_system') && isLivePage;
	const editorApi = isPresetRerunningOnTransition
		? editorApiSmootherTransitionsFG
		: editorApiStandard;
	const preset = isPresetRerunningOnTransition ? presetSmootherTransitionsFG : presetStandard;

	useEffect(() => {
		editorApi?.core?.actions.execute(
			editorApi?.placeholder?.commands.setPlaceholder(props.placeholder ?? ''),
		);
	}, [editorApi, props.placeholder]);

	const { setEditorAPI } = props;

	useEffect(() => {
		if (!setEditorAPI) {
			return;
		}
		editorAPIRef.current = editorApi;
		setEditorAPI(editorApi);
	}, [editorApi, setEditorAPI]);

	useEffect(() => {
		if (props.viewMode === undefined) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.editorViewMode?.commands?.updateViewMode(props.viewMode),
		);
	}, [props.viewMode, editorApi]);

	useEffect(() => {
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateEditPermission(props.hasEditPermission),
		);
	}, [props.hasEditPermission, editorApi]);

	useEffect(() => {
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateHasRequestedEditPermission(
				props.hasRequestedEditPermission || false,
			),
		);
	}, [props.hasRequestedEditPermission, editorApi]);

	const { enabled, autoHighlightEnabled, enablementSource } = editorPluginAIDefinitionsConfig;
	useEffect(() => {
		if (!fg('confluence_ai_access_policy')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.aiDefinitions?.commands?.updateState({
				enabled,
				autoHighlightEnabled,
				enablementSource,
			}),
		);
	}, [editorApi, enabled, autoHighlightEnabled, enablementSource]);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		flags: props.flags,
		entryPointLocation: 'editor-toolbar',
		resetInsertFunctionOnUnmount: true,
	});

	const loomProviderConfig: LoomProviderConfig = useMemo(
		() => ({ openLoomRecorder }),
		[openLoomRecorder],
	);

	useInitializeLoomPlugin({
		loomPlugin: editorApi?.loom as EditorAPI['loom'],
		providerConfig: loomProviderConfig,
	});

	return preset;
}
